import type { RenderActivation, RenderNode, RenderTree } from '@inf-monkeys-tech/monkeys';
import { compileRenderTree } from '@inf-monkeys-tech/monkeys/runtime';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RenderTreeHost } from './RenderTreeHost';
import type { RenderTreeHostProps } from './RenderTreeHost';

export interface RenderTreeRuntimeSnapshot {
  contract: 'RenderTreeRuntimeSnapshot';
  treeId: string;
  activeNodeIds: string[];
  manualQueryNodeIds: string[];
  selectedActivationId?: string;
}

export interface RenderTreeHistoryAdapter {
  read(): RenderTreeRuntimeSnapshot | undefined;
  replace(snapshot: RenderTreeRuntimeSnapshot): void;
  push(snapshot: RenderTreeRuntimeSnapshot): void;
  navigate(path: string, mode: 'push' | 'replace', snapshot: RenderTreeRuntimeSnapshot): void;
  subscribe(listener: (snapshot: RenderTreeRuntimeSnapshot | undefined) => void): () => void;
}

const STATE_KEY = '__monkeysRenderTree';

const readBrowserSnapshot = (): RenderTreeRuntimeSnapshot | undefined => {
  if (typeof window === 'undefined') return undefined;
  const value = window.history.state?.[STATE_KEY] as RenderTreeRuntimeSnapshot | undefined;
  return value?.contract === 'RenderTreeRuntimeSnapshot' ? value : undefined;
};

const withSnapshot = (snapshot: RenderTreeRuntimeSnapshot) => ({
  ...(typeof window === 'undefined' || !window.history.state || typeof window.history.state !== 'object'
    ? {}
    : window.history.state),
  [STATE_KEY]: snapshot,
});

const notifyBrowserHistory = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
};

export const browserRenderTreeHistoryAdapter: RenderTreeHistoryAdapter = {
  read: readBrowserSnapshot,
  replace(snapshot) {
    if (typeof window !== 'undefined') window.history.replaceState(withSnapshot(snapshot), '', window.location.href);
  },
  push(snapshot) {
    if (typeof window !== 'undefined') window.history.pushState(withSnapshot(snapshot), '', window.location.href);
  },
  navigate(path, mode, snapshot) {
    if (typeof window === 'undefined') return;
    window.history[mode === 'replace' ? 'replaceState' : 'pushState'](withSnapshot(snapshot), '', path);
    notifyBrowserHistory();
  },
  subscribe(listener) {
    if (typeof window === 'undefined') return () => undefined;
    const onPopState = () => listener(readBrowserSnapshot());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  },
};

export interface RenderTreeRuntimeHostProps
  extends Omit<
    RenderTreeHostProps,
    | 'activeNodeIds'
    | 'visibleNodeIds'
    | 'manualQueryNodeIds'
    | 'onActivate'
    | 'onManualQueryEnabledChange'
    | 'elementRefForNode'
  > {
  tree: RenderTree;
  historyAdapter?: RenderTreeHistoryAdapter | false;
  onActivate?: RenderTreeHostProps['onActivate'];
}

const scheduleFocus = (target: HTMLElement | undefined) => {
  if (!target?.isConnected) return;
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
  } else {
    target.focus({ preventScroll: true });
  }
};

export function RenderTreeRuntimeHost({
  tree,
  historyAdapter = browserRenderTreeHistoryAdapter,
  onActivate,
  onRuntimeStateChange,
  ...props
}: RenderTreeRuntimeHostProps) {
  const compiled = useMemo(() => compileRenderTree(tree), [tree]);
  const allNodeIds = useMemo(() => new Set(compiled.traversal.map((node) => node.nodeId)), [compiled]);
  const [activeNodeIds, setActiveNodeIds] = useState<Set<string>>(() => new Set(allNodeIds));
  const [manualQueryNodeIds, setManualQueryNodeIds] = useState<Set<string>>(() => new Set());
  const selectedActivationIdRef = useRef<string>();
  const activeNodeIdsRef = useRef(activeNodeIds);
  const manualQueryNodeIdsRef = useRef(manualQueryNodeIds);
  const focusReturnTargets = useRef(new Map<string, HTMLElement>());

  const snapshot = useCallback((
    nextActive = activeNodeIdsRef.current,
    nextManual = manualQueryNodeIdsRef.current,
    selectedActivationId = selectedActivationIdRef.current,
  ): RenderTreeRuntimeSnapshot => ({
    contract: 'RenderTreeRuntimeSnapshot',
    treeId: tree.treeId,
    activeNodeIds: [...nextActive],
    manualQueryNodeIds: [...nextManual],
    selectedActivationId,
  }), [tree.treeId]);

  const applySnapshot = useCallback((value: RenderTreeRuntimeSnapshot | undefined) => {
    if (!value || value.treeId !== tree.treeId) return false;
    if (value.activeNodeIds.some((nodeId) => !allNodeIds.has(nodeId))) return false;
    if (value.manualQueryNodeIds.some((nodeId) => !allNodeIds.has(nodeId))) return false;
    const active = new Set(value.activeNodeIds);
    if (!active.has(tree.rootNodeId)) return false;
    const manual = new Set(value.manualQueryNodeIds);
    selectedActivationIdRef.current = value.selectedActivationId;
    activeNodeIdsRef.current = active;
    manualQueryNodeIdsRef.current = manual;
    setActiveNodeIds(active);
    setManualQueryNodeIds(manual);
    return true;
  }, [allNodeIds, tree.rootNodeId, tree.treeId]);

  useEffect(() => {
    const initial = historyAdapter === false ? undefined : historyAdapter.read();
    if (!applySnapshot(initial)) {
      const active = new Set(allNodeIds);
      const manual = new Set<string>();
      activeNodeIdsRef.current = active;
      manualQueryNodeIdsRef.current = manual;
      selectedActivationIdRef.current = undefined;
      setActiveNodeIds(active);
      setManualQueryNodeIds(manual);
      historyAdapter !== false && historyAdapter.replace(snapshot(active, manual));
    }
    return historyAdapter === false ? undefined : historyAdapter.subscribe(applySnapshot);
  }, [allNodeIds, applySnapshot, historyAdapter, snapshot]);

  const selectNode = useCallback((node: RenderNode) => {
    const next = new Set(compiled.ancestorsOf(node.nodeId).map((ancestor) => ancestor.nodeId));
    const addDescendants = (current: RenderNode) => {
      next.add(current.nodeId);
      compiled.childrenOf(current.nodeId).forEach(addDescendants);
    };
    addDescendants(node);
    return next;
  }, [compiled]);

  const handleActivate = useCallback((activation: RenderActivation, node: RenderNode) => {
    if (node.lifecycle.focusReturn && typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      focusReturnTargets.current.set(node.nodeId, document.activeElement);
    }
    selectedActivationIdRef.current = activation.activationId;
    const nextActive = activation.mode === 'select' || activation.mode === 'inline'
      ? selectNode(node)
      : activeNodeIdsRef.current;
    activeNodeIdsRef.current = nextActive;
    setActiveNodeIds(nextActive);
    const nextSnapshot = snapshot(nextActive);

    if (activation.mode === 'navigate') {
      if (!activation.targetPath) throw new TypeError(`Navigate activation ${activation.activationId} has no targetPath.`);
      if (historyAdapter !== false) historyAdapter.navigate(activation.targetPath, activation.history ?? 'push', nextSnapshot);
    } else if (historyAdapter !== false && node.lifecycle.deepLink) {
      if (activation.history === 'push') historyAdapter.push(nextSnapshot);
      else historyAdapter.replace(nextSnapshot);
    }
    onActivate?.(activation, node);
  }, [historyAdapter, onActivate, selectNode, snapshot]);

  const handleManualQueryEnabledChange = useCallback((enabled: boolean, node: RenderNode) => {
    const next = new Set(manualQueryNodeIdsRef.current);
    if (enabled) next.add(node.nodeId);
    else next.delete(node.nodeId);
    manualQueryNodeIdsRef.current = next;
    setManualQueryNodeIds(next);
    historyAdapter !== false && historyAdapter.replace(snapshot(activeNodeIdsRef.current, next));
  }, [historyAdapter, snapshot]);

  const previousActive = useRef(activeNodeIds);
  useEffect(() => {
    for (const nodeId of previousActive.current) {
      if (activeNodeIds.has(nodeId)) continue;
      const node = compiled.nodesById.get(nodeId);
      if (node?.lifecycle.focusReturn) scheduleFocus(focusReturnTargets.current.get(nodeId));
    }
    previousActive.current = activeNodeIds;
  }, [activeNodeIds, compiled.nodesById]);

  return (
    <RenderTreeHost
      {...props}
      tree={tree}
      activeNodeIds={activeNodeIds}
      visibleNodeIds={activeNodeIds}
      manualQueryNodeIds={manualQueryNodeIds}
      onActivate={handleActivate}
      onManualQueryEnabledChange={handleManualQueryEnabledChange}
      onRuntimeStateChange={onRuntimeStateChange}
    />
  );
}
